// RateEventPage — a bookkeeper rates an event in TSB's conference directory
// without signing in: rate first, confirm by email (Michael, 24 Oct 2026,
// mca_4d4b0213f480; mct_0bc60295-c2f).
//
//   /rate/:slug          — stars, an optional comment, name and email, Send
//   /rate/confirm?t=...  — the page the emailed link opens; the Confirm button saves it
//
// Nothing counts until the person presses Confirm on the second page. The link
// only opens that page, never saves on its own: mail scanners open every link
// and run scripts. The event is the CC series slug the website pages use
// (thesuccessfulbookkeeper.com/bookkeeping-conferences/<slug>).
// Rationale: seeds/sops/sop-ee-industry-event-reviews.md.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { useBrandGtm } from './lib/useBrandGtm';

const API = import.meta.env.VITE_API_BASE_URL as string;
const REVIEWS = `${API}/api/public/industry-event-reviews`;
const eventPageUrl = (slug: string) => `https://www.thesuccessfulbookkeeper.com/bookkeeping-conferences/${encodeURIComponent(slug)}`;

const STAR_LABELS = ['Poor', 'Fair', 'Good', 'Very good', 'Excellent'];
const MAX_COMMENT = 2000;

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.12, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const label: CSSProperties = { display: 'block', fontSize: 15, fontWeight: 600, color: '#0A0A2E', margin: '0 0 6px' };
const hint: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5, margin: '6px 0 0' };
const field: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', fontFamily: 'inherit' };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: '2px solid #F05A28', borderRadius: 10, padding: '13px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };
const linkBtn: CSSProperties = { ...btn, display: 'inline-block', textDecoration: 'none' };
const errText: CSSProperties = { color: '#b91c1c', fontSize: 15, margin: '12px 0 0' };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

function Stars({ value, onPick, size = 40 }: { value: number; onPick?: (n: number) => void; size?: number }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div role={onPick ? 'radiogroup' : 'img'} aria-label={onPick ? 'Your rating' : `${value} out of 5 stars`} style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= shown;
        const star = <span aria-hidden style={{ fontSize: size, lineHeight: 1, color: on ? '#F05A28' : '#d6d3cc' }}>{on ? '★' : '☆'}</span>;
        if (!onPick) return <span key={n}>{star}</span>;
        return (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? 's' : ''}, ${STAR_LABELS[n - 1]}`}
            onClick={() => onPick(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer' }}>
            {star}
          </button>
        );
      })}
    </div>
  );
}

export function RateEventPage() {
  const { slug = '' } = useParams();
  useBrandGtm();
  const [eventName, setEventName] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'not_found' | 'error' | 'sent'>('loading');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${REVIEWS}/event?slug=${encodeURIComponent(slug)}`).then(async (r) => {
      if (r.status === 404 || r.status === 400) { setState('not_found'); return; }
      if (!r.ok) { setState('error'); return; }
      const d = (await r.json()) as { name: string };
      setEventName(d.name);
      document.title = `Rate ${d.name} | The Successful Bookkeeper`;
      setState('ready');
    }).catch(() => setState('error'));
  }, [slug]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!rating) { setErr('Pick a rating from 1 to 5 stars.'); return; }
    if (!firstName.trim() || !lastName.trim()) { setErr('Please enter your first and last name.'); return; }
    if (!email.trim()) { setErr('Please enter your email address.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${REVIEWS}/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, rating, comment, first_name: firstName, last_name: lastName, email, hp }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.error ?? 'Something went wrong. Please try again in a moment.'); return; }
      setState('sent');
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'not_found') {
    return (
      <Shell>
        <h1 style={h1}>We could not find that event</h1>
        <p style={lead}>It may not be in our directory yet. Have a look at the full list of bookkeeping conferences.</p>
        <a style={linkBtn} href="https://www.thesuccessfulbookkeeper.com/bookkeeping-conferences">See all conferences</a>
      </Shell>
    );
  }
  if (state === 'error' || !eventName) {
    return (
      <Shell>
        <h1 style={h1}>Something went wrong</h1>
        <p style={lead}>We could not load this event just now. Please try again in a few minutes.</p>
      </Shell>
    );
  }
  if (state === 'sent') {
    return (
      <Shell>
        <h1 style={h1}>Check your email</h1>
        <p style={lead}>
          We sent a link to <strong>{email.trim()}</strong>. Open it and press <strong>Confirm my rating</strong>, and
          your rating of {eventName} will count.
        </p>
        <p style={{ ...lead, fontSize: 15 }}>
          Nothing arrived after a few minutes? Check your spam or promotions folder. The link works for 3 days.
        </p>
        <a style={{ color: '#F05A28', fontWeight: 600 }} href={eventPageUrl(slug)}>Back to {eventName}</a>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={h1}>How was {eventName}?</h1>
      <p style={lead}>Your rating helps other bookkeepers decide which conferences are worth their time and money.</p>
      <form onSubmit={send} noValidate>
        <div style={card}>
          <span style={label}>Your rating</span>
          <Stars value={rating} onPick={setRating} />
          <p style={{ ...hint, minHeight: 21 }}>{rating ? STAR_LABELS[rating - 1] : 'Tap a star'}</p>
        </div>
        <div style={card}>
          <label style={label} htmlFor="rate-comment">What should other bookkeepers know? (optional)</label>
          <textarea id="rate-comment" style={{ ...field, minHeight: 120 }} value={comment} maxLength={MAX_COMMENT}
            onChange={(e) => setComment(e.target.value)} placeholder="The sessions, the people, whether you would go again…" />
          <p style={hint}>Our team reads every comment before it appears on the event page.</p>
        </div>
        <div style={card}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label} htmlFor="rate-first">First name</label>
              <input id="rate-first" style={field} autoComplete="given-name" value={firstName} maxLength={60} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <label style={label} htmlFor="rate-last">Last name</label>
              <input id="rate-last" style={field} autoComplete="family-name" value={lastName} maxLength={60} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <label style={label} htmlFor="rate-email">Email</label>
          <input id="rate-email" type="email" style={field} autoComplete="email" value={email} maxLength={254} onChange={(e) => setEmail(e.target.value)} />
          <p style={hint}>We will email you a link to confirm your rating. Only your first name and last initial are shown with it.</p>
          {/* Left empty by people; bots fill it. */}
          <input type="text" name="company_website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
            aria-hidden style={{ position: 'absolute', left: -10000, width: 1, height: 1, opacity: 0 }} />
        </div>
        <button type="submit" style={{ ...btn, opacity: busy ? 0.7 : 1 }} disabled={busy}>{busy ? 'Sending…' : 'Send my rating'}</button>
        {err && <p style={errText} role="alert">{err}</p>}
      </form>
    </Shell>
  );
}

interface PendingInfo { state: 'ready' | 'confirmed' | 'expired'; slug: string; event_name: string | null; rating: number; has_comment: boolean }

export function RateConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [info, setInfo] = useState<PendingInfo | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'done' | 'expired' | 'invalid' | 'error'>('loading');
  const [commentWaiting, setCommentWaiting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const once = useRef(false);

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    if (!token) { setState('invalid'); return; }
    // Reads only. The rating is saved by the button below, never by opening the link.
    fetch(`${REVIEWS}/pending?t=${encodeURIComponent(token)}`).then(async (r) => {
      if (r.status === 404) { setState('invalid'); return; }
      if (!r.ok) { setState('error'); return; }
      const d = (await r.json()) as PendingInfo;
      setInfo(d);
      setState(d.state === 'confirmed' ? 'done' : d.state);
    }).catch(() => setState('error'));
  }, [token]);

  const confirm = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${REVIEWS}/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }) });
      const d = await r.json().catch(() => ({}));
      if (r.status === 410) { setState('expired'); return; }
      if (!r.ok) { setErr(d?.error ?? 'Something went wrong. Please try again in a moment.'); return; }
      setCommentWaiting(!!d.comment_waiting);
      setState('done');
      window.scrollTo(0, 0);
    } catch {
      setErr('We could not reach our system. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const name = info?.event_name ?? 'this event';
  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'invalid') {
    return (
      <Shell>
        <h1 style={h1}>This link no longer works</h1>
        <p style={lead}>It may have been replaced by a newer one. Use the link in the most recent email we sent you, or rate the event again.</p>
        <a style={linkBtn} href="https://www.thesuccessfulbookkeeper.com/bookkeeping-conferences">See all conferences</a>
      </Shell>
    );
  }
  if (state === 'expired') {
    return (
      <Shell>
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>Confirm links work for 3 days. Rate {name} again and we will send you a fresh one.</p>
        {info?.slug && <a style={linkBtn} href={`/rate/${encodeURIComponent(info.slug)}`}>Rate {name} again</a>}
      </Shell>
    );
  }
  if (state === 'error' || !info) {
    return (
      <Shell>
        <h1 style={h1}>Something went wrong</h1>
        <p style={lead}>Please open the link from your email again in a few minutes.</p>
      </Shell>
    );
  }
  if (state === 'done') {
    return (
      <Shell>
        <h1 style={h1}>Your rating is in</h1>
        <p style={lead}>
          Thank you for rating {name}. It now counts toward the event's rating.
          {commentWaiting && ' Your comment will appear on the event page once our team has read it.'}
        </p>
        <a style={linkBtn} href={eventPageUrl(info.slug)}>Back to {name}</a>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={h1}>Confirm your rating</h1>
      <p style={lead}>You rated {name}:</p>
      <div style={card}>
        <Stars value={info.rating} size={34} />
        <p style={hint}>{STAR_LABELS[info.rating - 1]}{info.has_comment ? ', with a comment' : ''}</p>
      </div>
      <button type="button" style={{ ...btn, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={confirm}>{busy ? 'Confirming…' : 'Confirm my rating'}</button>
      {err && <p style={errText} role="alert">{err}</p>}
    </Shell>
  );
}
